import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import Checklist from '../components/Checklist';

// ── COLOUR SYSTEM ─────────────────────────────────────────────
// Teal    (#14b8a6) = recommended/calculated value
// Green   (#22c55e) = within good range / check passed
// Amber   (#f59e0b) = zone temperatures / warning
// Red     (#ef4444) = outside range / check failed / critical
// White   (#f0f2f5) = neutral value
// Muted   (#6b7280) = label / secondary info

const INFO = {
  zones:           'Barrel zone temperatures calculated using a progressive step-down profile from the front (nozzle side) to the feed zone. Front zone = target melt − estimated shear heat. Each zone steps down 10–15°C toward feed to ensure uniform melting.',
  mould_temp:      'Mould temperature taken directly from the material datasheet recommendation. Controls surface quality, shrinkage and cycle time.',
  melt_temp:       'Estimated melt temperature = front zone + estimated shear heat contribution. Must fall within the material\'s processing window.',
  fill_time:       'Estimated fill time based on shot volume and screw cross-sectional area at 70% of max injection speed. Adjust if gate shear rate check fails.',
  inj_pressure:    'Maximum injection pressure calculated from flow length, material viscosity (MFI), and mould geometry. Machine limit applied as ceiling.',
  inj_stages:      'Multi-stage injection profile. Stage 1: fast fill. Stage 2: slow approach to V→P. Stage 3: decelerate before switchover. Speeds are % of max machine speed.',
  vp:              'V→P switchover point — where the machine transitions from velocity control to pressure control. Position switchover is most common and most stable.',
  shear_rate:      'Shear rate at the gate. Must stay below the material\'s maximum shear rate to avoid degradation, burning, or splay marks.',
  gate_freeze:     'Time for the gate to solidify after injection. Hold time must exceed gate freeze time to prevent backflow into the gate.',
  hold_stages:     'Hold pressure profile. Stage 1: high pressure to pack out shrinkage. Steps down progressively to avoid over-packing.',
  suckback:        'Screw retract after recovery to reduce nozzle drool. Value based on MFI — higher flow materials need more suckback.',
  cooling_min:     'Minimum cooling time calculated from Fourier\'s law of heat conduction. Uses wall thickness, thermal diffusivity, melt temp and ejection temp.',
  cooling_rec:     'Recommended cooling time = minimum × 1.20 safety factor. Start here and reduce by 0.5s increments once process is stable.',
  flow_rate:       'Cooling water flow rate per circuit required to remove the heat from one shot within the cooling time. Turbulent flow (Re > 4000) is recommended.',
  cooling_zones:   'Per-zone circuit analysis using the actual circuit geometry. Shows required flow rate, velocity, Reynolds number and pressure drop per zone. Turbulent flow (Re > 4000) is essential — laminar flow reduces cooling efficiency by up to 50%. Temperature recommendation per zone accounts for which half the part sticks to on opening.',
  screw_speed:     'Screw speed calculated as midpoint of the overlap between machine range and material recommendation. Higher MFI materials tolerate higher speeds.',
  back_pressure:   'Back pressure calculated as midpoint of machine × material overlap. Higher back pressure = better melt homogeneity but longer recovery.',
  recovery:        'Time for screw to recover the next shot. Must be less than cooling time — otherwise recovery extends the cycle.',
  shot_barrel:     'Shot volume as % of barrel volume. Ideal range is 20–80%. Below 20%: long residence time risk. Above 80%: inconsistent shot weight risk.',
  residence:       'Time material spends in the barrel at melt temperature. Must stay below 360s (6 min) to prevent thermal degradation.',
  clamp:           'Minimum clamp force = projected area × cavity pressure × number of cavities. Suggested = minimum × 1.10 safety factor.',
  closing:         'Mould closing profile. Fast close until near parting line, slow approach to protect mould faces, high-pressure lock to set clamp tonnage.',
  opening:         'Mould opening profile. Slow break to overcome adhesion, fast open, decelerate before end of stroke.',
  ejection:        'Ejection parameters. Delay allows part to shrink onto core before ejection. Speed based on MFI — stiffer materials need slower ejection.',
  shot_vol:        'Shot volume = shot weight ÷ melt density. This is the actual volume of plastic injected per cycle in cm³.',
  dosing_stroke:   'Dosing stroke = shot volume ÷ screw cross-sectional area. This is how far the screw travels back during recovery to accumulate the next shot.',
  clamp_sugg:      'Suggested clamp force = minimum clamp force × 1.10 safety factor. Set your machine tonnage to at least this value.',
  ej_stroke:       'Ejector stroke taken directly from the mould record. Must be sufficient to fully clear the part from the core.',
  ej_fwd:          'Ejector forward speed based on MFI — higher flow materials produce more flexible parts that tolerate faster ejection. Increase if parts stick.',
  ej_ret:          'Ejector retract speed — typically 30-40% faster than forward speed since the ejectors are unloaded on retract.',
  ej_pulses:       'Number of ejector pulses. Single pulse is standard. Increase to 2–3 if parts stick to the core or have deep ribs.',
};

export default function Parameters() {
  const { isDirector, can } = useAuth();
  const navigate = useNavigate();
  const [view, setView]           = useState('landing'); // landing | wizard | results
  const [step, setStep]           = useState(1);
  const [machines, setMachines]   = useState([]);
  const [moulds, setMoulds]       = useState([]);
  const [recipes, setRecipes]     = useState([]);
  const [optimisations, setOptimisations]   = useState([]);
  const [selected, setSelected]   = useState({ machine: null, mould: null, recipe: null });
  const [calculating, setCalculating] = useState(false);
  const [error, setError]         = useState('');
  const [showMachineForm, setShowMachineForm] = useState(false);
  const [showMouldForm, setShowMouldForm]     = useState(false);
  const [showRecipeForm, setShowRecipeForm]   = useState(false);
  const [missingMaterial, setMissingMaterial]       = useState(null);
  const [showMaterialModal, setShowMaterialModal]   = useState(false);
  useEffect(() => {
    api.get('/api/v1/machines').then(r => setMachines(r.data));
    api.get('/api/v1/moulds').then(r => setMoulds(r.data));
  }, []);

  useEffect(() => {
    if (view === 'landing') {
      api.get('/api/v1/optimisations').then(r => setOptimisations(r.data)).catch(() => {});
    }
  }, [view]);

  function startNew() { setView('wizard'); setStep(1); setSelected({ machine: null, mould: null, recipe: null }); setError(''); }

  function selectMachine(m) { setSelected(s => ({ ...s, machine: m })); setStep(2); }
  function selectMould(m) {
    setSelected(s => ({ ...s, mould: m, recipe: null }));
    api.get(`/api/v1/recipes/mould/${m.id}`).then(r => { setRecipes(r.data); setStep(3); });
  }
  function selectRecipe(recipe) {
    setSelected(s => ({ ...s, recipe }));
    setCalculating(true); setError(''); setMissingMaterial(null); setShowMaterialModal(false);
    const machine = selected.machine;
    const mould   = selected.mould;

    // Always recalculate with current data — check and material fetch run in parallel
    let fetchedMaterial = null;
    Promise.all([
      api.get(`/api/v1/optimisations/check?machine_id=${machine.id}&mould_id=${mould.id}&recipe_id=${recipe.id}`),
      api.get(`/api/v1/materials/${recipe.material_id}`),
    ])
      .then(([checkRes, matRes]) => {
        fetchedMaterial = matRes.data;
        return api.post('/api/v1/parameters/calculate', { machine, mould, material: matRes.data })
          .then(calcRes => ({ calcRes, existingId: checkRes.data?.id || null }));
      })
      .then(({ calcRes, existingId }) => {
        if (calcRes.data.error) {
          setError(calcRes.data.error);
          if (calcRes.data.error.includes('physics constants') && can('manage')) { setMissingMaterial(fetchedMaterial); setShowMaterialModal(true); }
          setCalculating(false);
          return null;
        }
        // An Operator reads the sheet that is there; making or adding to one is a
        // Setter's or a Director's (3 Nov 2026).
        if (!can('edit_process')) {
          if (existingId) return existingId;
          setError('There is no process sheet for this machine, mould and recipe yet. A setter or a director makes it.');
          setCalculating(false);
          return null;
        }
        if (existingId) {
          // Add fresh recalculated version to existing optimisation
          return api.post(`/api/v1/optimisations/${existingId}/versions`, {
            params: calcRes.data, type: 'calculated', reason: 'Recalculated from wizard',
          }).then(() => existingId);
        }
        return api.post('/api/v1/optimisations', {
          machine_id: machine.id, mould_id: mould.id, recipe_id: recipe.id, params: calcRes.data,
        }).then(r => r.data.id);
      })
      .then(id => { if (id) navigate(`/process/optimise/${id}`); })
      .catch(err => {
        if (err.response?.status === 409 && err.response?.data?.existing_id) {
          navigate(`/process/optimise/${err.response.data.existing_id}`);
          return;
        }
        const msg = err.response?.data?.error || err.message || 'Calculation failed';
        setError(msg);
        setCalculating(false);
        console.error('Process setup error:', err.response?.data || err);
      });
  }

  if (view === 'wizard') return (
    <WizardView
      step={step} setStep={setStep}
      machines={machines} moulds={moulds} recipes={recipes}
      selected={selected}
      selectMachine={selectMachine}
      selectMould={selectMould}
      selectRecipe={selectRecipe}
      calculating={calculating}
      error={error}
      isDirector={isDirector()}
      onBack={() => setView('landing')}
      showMachineForm={showMachineForm} setShowMachineForm={setShowMachineForm}
      showMouldForm={showMouldForm}     setShowMouldForm={setShowMouldForm}
      showRecipeForm={showRecipeForm}   setShowRecipeForm={setShowRecipeForm}
      onMachineSaved={m => { setMachines(ms => [...ms, m]); setShowMachineForm(false); selectMachine(m); }}
      onMouldSaved={m  => { setMoulds(ms => [...ms, m]);   setShowMouldForm(false);   selectMould(m); }}
      onRecipeSaved={r => { setRecipes(rs => [...rs, r]);  setShowRecipeForm(false);  selectRecipe(r); }}
      missingMaterial={missingMaterial}
      showMaterialModal={showMaterialModal}
      onMaterialOpen={() => setShowMaterialModal(true)}
      onMaterialSaved={() => { setMissingMaterial(null); setShowMaterialModal(false); setError(''); selectRecipe(selected.recipe); }}
      onMaterialDismiss={() => setShowMaterialModal(false)}
    />
  );

  // Landing
  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Setup Parameters</h1>
          <p style={s.sub}>Calculate optimal starting parameters for any machine, mould and recipe combination</p>
        </div>
      </div>

      {/* New calculation card */}
      <div style={s.newCalcCard} onClick={startNew}>
        <div style={s.newCalcIcon}>🔬</div>
        <div>
          <div style={s.newCalcTitle}>{can('edit_process') ? 'New Calculation' : 'Find a process sheet'}</div>
          <div style={s.newCalcSub}>{can('edit_process') ? 'Select machine → mould → recipe → get parameters' : 'Select machine → mould → recipe → open its sheet'}</div>
        </div>
        <div style={s.newCalcArrow}>→</div>
      </div>

      {/* Optimisation library */}
      <div style={s.sectionTitle}>Optimisation Library</div>
      {optimisations.length === 0 ? (
        <div style={s.emptyBox}>
          <div style={s.emptyIcon}>🔬</div>
          <div style={s.emptyText}>No optimisations yet</div>
          <div style={s.emptySub}>{can('edit_process') ? 'Start a New Calculation — parameters are automatically saved as Version 1' : 'A setter or a director makes them. Each one shows here when it is saved.'}</div>
        </div>
      ) : (
        <div style={s.goldenGrid}>
          {optimisations.map(opt => (
            <div key={opt.id} style={{ ...s.goldenCard, cursor: 'pointer' }}
              onClick={() => navigate(`/process/optimise/${opt.id}`)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={s.goldenRef}>{opt.mould?.ref}</div>
                {opt.has_golden && <span style={{ fontSize: '12px' }}>⭐</span>}
              </div>
              <div style={s.goldenName}>{opt.mould?.name}</div>
              <div style={s.goldenMeta}>{opt.machine?.name} · {opt.recipe?.material?.name}</div>
              <div style={s.goldenDate}>
                {opt.version_count} version{opt.version_count !== 1 ? 's' : ''}
                {' · '}{new Date(opt.updated_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── WIZARD ────────────────────────────────────────────────────
function WizardView({ step, setStep, machines, moulds, recipes, selected, selectMachine, selectMould, selectRecipe, calculating, error, isDirector, onBack, showMachineForm, setShowMachineForm, showMouldForm, setShowMouldForm, showRecipeForm, setShowRecipeForm, onMachineSaved, onMouldSaved, onRecipeSaved, missingMaterial, showMaterialModal, onMaterialOpen, onMaterialSaved, onMaterialDismiss }) {
  const canAddRecipe = useAuth().can('add_recipe');

  const stepLabels = ['Machine', 'Mould', 'Recipe', 'Results'];

  return (
    <div style={s.page}>
      {showMachineForm && <QuickAddModal title="Add Machine" onClose={() => setShowMachineForm(false)}><MachineQuickForm onSaved={onMachineSaved} onCancel={() => setShowMachineForm(false)} /></QuickAddModal>}
      {showMouldForm   && <QuickAddModal title="Add Mould"   onClose={() => setShowMouldForm(false)}><MouldQuickForm   onSaved={onMouldSaved}   onCancel={() => setShowMouldForm(false)} /></QuickAddModal>}
      {showRecipeForm  && <QuickAddModal title="Add Recipe"  onClose={() => setShowRecipeForm(false)}><RecipeQuickForm  mouldId={selected.mould?.id} onSaved={onRecipeSaved} onCancel={() => setShowRecipeForm(false)} /></QuickAddModal>}
      {missingMaterial && showMaterialModal && <MaterialPhysicsModal material={missingMaterial} onSaved={onMaterialSaved} onClose={onMaterialDismiss} />}

      <div style={s.wizardHeader}>
        <button style={s.backBtn} onClick={step === 1 ? onBack : () => setStep(step - 1)}>← Back</button>
        <div>
          <div style={s.title}>New Calculation</div>
          <div style={s.stepIndicator}>
            {stepLabels.map((label, i) => (
              <span key={label} style={{ ...s.stepDot, background: i+1 === step ? '#14b8a6' : i+1 < step ? '#0d9488' : '#2a3040', color: i+1 <= step ? '#fff' : '#6b7280' }}>
                {i+1 < step ? '✓' : i+1}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={s.stepLabel}>Step {step} of 3 — Select {stepLabels[step-1]}</div>

      {step === 1 && (
        <>
          <div style={s.selectGrid}>
            {machines.map(m => (
              <div key={m.id} style={s.selectCard} onClick={() => selectMachine(m)}>
                <div style={s.selectCardTitle}>{m.name}</div>
                <div style={s.selectCardSub}>{m.site} · {m.tonnes}T · ⌀{m.screw_mm}mm</div>
              </div>
            ))}
          </div>
          {isDirector && <button style={s.addNewBtn} onClick={() => setShowMachineForm(true)}>+ Add New Machine</button>}
        </>
      )}

      {step === 2 && (
        <>
          <div style={s.selectGrid}>
            {moulds.map(m => (
              <div key={m.id} style={s.selectCard} onClick={() => selectMould(m)}>
                <div style={s.selectCardTitle}>{m.ref}</div>
                <div style={s.selectCardSub}>{m.name} · {m.cavities} cav</div>
              </div>
            ))}
          </div>
          {isDirector && <button style={s.addNewBtn} onClick={() => setShowMouldForm(true)}>+ Add New Mould</button>}
        </>
      )}

      {step === 3 && (
        <>
          {calculating ? (
            <div style={s.calculating}>Calculating parameters...</div>
          ) : (
            <>
              {recipes.length === 0 ? (
                <div style={s.emptyBox}>
                  <div style={s.emptyText}>No recipes for {selected.mould?.ref}</div>
                  <div style={s.emptySub}>Create a recipe to define the material blend</div>
                  <button style={{ ...s.addNewBtn, marginTop: '12px', display: canAddRecipe ? undefined : 'none' }} onClick={() => setShowRecipeForm(true)}>+ Create Recipe</button>
                </div>
              ) : (
                <>
                  <div style={s.selectGrid}>
                    {recipes.map(r => (
                      <div key={r.id} style={s.selectCard} onClick={() => selectRecipe(r)}>
                        <div style={s.selectCardTitle}>{r.name}</div>
                        <div style={s.selectCardSub}>{r.material?.name}</div>
                        {(r.recipe_components || []).map(c => (
                          <div key={c.id} style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                            <span style={{ fontSize: '8px', background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534', color: '#fff', padding: '1px 4px', borderRadius: '3px', marginRight: '4px' }}>
                              {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
                            </span>
                            {c.material?.name} · {c.percentage}%
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  {canAddRecipe && <button style={s.addNewBtn} onClick={() => setShowRecipeForm(true)}>+ Create New Recipe</button>}
                </>
              )}
              {error && (
                <div style={{ ...s.errorBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span>{error}</span>
                  {missingMaterial && (
                    <button style={{ background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={onMaterialOpen}>
                      Complete {missingMaterial.name} data →
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── QUICK ADD MODAL ───────────────────────────────────────────
function QuickAddModal({ title, onClose, children }) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{title}</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>{children}</div>
      </div>
    </div>
  );
}

function MachineQuickForm({ onSaved, onCancel }) {
  const [form, setForm] = useState({ name: '', site: '', tonnes: '', screw_mm: '', barrel_cm3: '', max_inj_bar: '', screw_spd_min: '10', screw_spd_max: '200', back_pres_min: '5', back_pres_max: '150', max_stg: '5', hld_stg: '3', mv_stg: '3', vp_type: 'position', status: 'available' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  function save() {
    setSaving(true);
    const payload = { ...form };
    ['tonnes','screw_mm','barrel_cm3','max_inj_bar','screw_spd_min','screw_spd_max','back_pres_min','back_pres_max','max_stg','hld_stg','mv_stg'].forEach(k => { payload[k] = payload[k] ? parseFloat(payload[k]) : null; });
    api.post('/api/v1/machines', payload).then(r => onSaved(r.data)).catch(() => setSaving(false));
  }
  return (
    <div>
      <QField label="Machine name *" value={form.name} onChange={v => set('name', v)} />
      <QField label="Site / Line" value={form.site} onChange={v => set('site', v)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <QField label="Clamp force (T)" value={form.tonnes} onChange={v => set('tonnes', v)} type="number" />
        <QField label="Screw diameter (mm)" value={form.screw_mm} onChange={v => set('screw_mm', v)} type="number" />
        <QField label="Barrel volume (cm³)" value={form.barrel_cm3} onChange={v => set('barrel_cm3', v)} type="number" />
        <QField label="Max inj pressure (bar)" value={form.max_inj_bar} onChange={v => set('max_inj_bar', v)} type="number" />
        <QField label="Screw speed min (rpm)" value={form.screw_spd_min} onChange={v => set('screw_spd_min', v)} type="number" />
        <QField label="Screw speed max (rpm)" value={form.screw_spd_max} onChange={v => set('screw_spd_max', v)} type="number" />
        <QField label="Back pressure min (bar)" value={form.back_pres_min} onChange={v => set('back_pres_min', v)} type="number" />
        <QField label="Back pressure max (bar)" value={form.back_pres_max} onChange={v => set('back_pres_max', v)} type="number" />
      </div>
      <div style={s.modalFooter}>
        <button style={s.backBtn} onClick={onCancel}>Cancel</button>
        <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Machine'}</button>
      </div>
    </div>
  );
}

function MouldQuickForm({ onSaved, onCancel }) {
  const [form, setForm] = useState({ ref: '', name: '', customer: '', cavities: '', pa_cm2: '', wmax_mm: '', sw_g: '', pw_g: '', rw_g: '', gate_type: 'edge', gw_mm: '', gd_mm: '', ej_stroke_mm: '', ej_pulses: '1', circuits: '2', status: 'active' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  function save() {
    setSaving(true);
    const payload = { ...form };
    ['cavities','pa_cm2','wmax_mm','sw_g','pw_g','rw_g','gw_mm','gd_mm','ej_stroke_mm','ej_pulses','circuits'].forEach(k => { payload[k] = payload[k] ? parseFloat(payload[k]) : null; });
    api.post('/api/v1/moulds', payload).then(r => onSaved(r.data)).catch(() => setSaving(false));
  }
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <QField label="Mould ref *" value={form.ref} onChange={v => set('ref', v)} />
        <QField label="Mould name *" value={form.name} onChange={v => set('name', v)} />
        <QField label="Customer" value={form.customer} onChange={v => set('customer', v)} />
        <QField label="Cavities" value={form.cavities} onChange={v => set('cavities', v)} type="number" />
        <QField label="Projected area (cm²)" value={form.pa_cm2} onChange={v => set('pa_cm2', v)} type="number" />
        <QField label="Wall max (mm)" value={form.wmax_mm} onChange={v => set('wmax_mm', v)} type="number" />
        <QField label="Shot weight (g)" value={form.sw_g} onChange={v => set('sw_g', v)} type="number" />
        <QField label="Part weight (g)" value={form.pw_g} onChange={v => set('pw_g', v)} type="number" />
        <QField label="Gate width (mm)" value={form.gw_mm} onChange={v => set('gw_mm', v)} type="number" />
        <QField label="Gate depth (mm)" value={form.gd_mm} onChange={v => set('gd_mm', v)} type="number" />
        <QField label="Ejector stroke (mm)" value={form.ej_stroke_mm} onChange={v => set('ej_stroke_mm', v)} type="number" />
        <QField label="Cooling circuits" value={form.circuits} onChange={v => set('circuits', v)} type="number" />
      </div>
      <div style={s.modalFooter}>
        <button style={s.backBtn} onClick={onCancel}>Cancel</button>
        <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Mould'}</button>
      </div>
    </div>
  );
}

function RecipeQuickForm({ mouldId, onSaved, onCancel }) {
  const [materials, setMaterials] = useState([]);
  const [form, setForm]           = useState({ name: '', material_id: '' });
  const [components, setComponents] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => { api.get('/api/v1/materials').then(r => setMaterials(r.data)); }, []);

  const baseMats = materials.filter(m => (m.type || 'material') === 'material');
  const mbs      = materials.filter(m => m.type === 'masterbatch');
  const adds     = materials.filter(m => m.type === 'additive');

  function addComp(type) { setComponents(c => [...c, { material_id: '', type, percentage: '' }]); }
  function updComp(i, k, v) { setComponents(c => c.map((x, idx) => idx !== i ? x : { ...x, [k]: v })); }
  function remComp(i) { setComponents(c => c.filter((_, idx) => idx !== i)); }

  function save() {
    if (!form.name || !form.material_id) { setError('Name and base material required'); return; }
    setSaving(true);
    api.post('/api/v1/recipes', {
      mould_id: mouldId, material_id: parseInt(form.material_id), name: form.name,
      components: components.filter(c => c.material_id && c.percentage).map(c => ({ material_id: parseInt(c.material_id), type: c.type, percentage: parseFloat(c.percentage) })),
    }).then(r => onSaved(r.data)).catch(() => { setError('Save failed'); setSaving(false); });
  }

  return (
    <div>
      {error && <div style={s.errorBox}>{error}</div>}
      <QField label="Recipe name *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
      <div style={s.formField}>
        <label style={s.formLabel}>Base material *</label>
        <select style={s.formInput} value={form.material_id} onChange={e => setForm(f => ({ ...f, material_id: e.target.value }))}>
          <option value="">— Select —</option>
          {baseMats.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>
      <div style={{ marginTop: '8px' }}>
        <div style={s.formLabel}>Masterbatches & Additives</div>
        {components.map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534', color: '#fff' }}>
              {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
            </span>
            <select style={{ ...s.formInput, flex: 2 }} value={c.material_id} onChange={e => updComp(i, 'material_id', e.target.value)}>
              <option value="">— Select —</option>
              {(c.type === 'masterbatch' ? mbs : adds).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <input type="number" placeholder="%" style={{ ...s.formInput, width: '60px' }} value={c.percentage} onChange={e => updComp(i, 'percentage', e.target.value)} />
            <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }} onClick={() => remComp(i)}>×</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button style={s.addComponentBtn} onClick={() => addComp('masterbatch')}>+ Masterbatch</button>
          <button style={s.addComponentBtn} onClick={() => addComp('additive')}>+ Additive</button>
        </div>
      </div>
      <div style={s.modalFooter}>
        <button style={s.backBtn} onClick={onCancel}>Cancel</button>
        <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save Recipe'}</button>
      </div>
    </div>
  );
}

function QField({ label, value, onChange, type = 'text' }) {
  return (
    <div style={s.formField}>
      <label style={s.formLabel}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} style={s.formInput} />
    </div>
  );
}

// ── RESULTS PAGE ──────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function ParameterResults({ result, machine, mould, recipe, fontSize, onFontChange, currentFont, onBack, onNew, onReturnToLanding, onMarkGolden, goldenMarked, goldenJobId }) {  const [expandedCheck, setExpandedCheck] = useState(null);
  useEffect(() => {
    return () => { document.body.style.zoom = '100%'; };
  }, []);
  const [viewMode, setViewMode] = useState('full'); // full | steps
  const [currentStep, setCurrentStep] = useState(0);
  const [showChecklist, setShowChecklist] = useState(null); // null | 'machine' | 'mould' | 'fai'
  const checks = result.checks || {};
  const anyFail = Object.values(checks).some(c => c.pass === false);

  const checkDetails = {
    melt_in_window:      { severity: 'amber', fix: 'Adjust barrel zone temperatures. Increase front zone temperature if melt is too cold, decrease if too hot.' },
    residence_time:      { severity: 'red',   fix: 'Residence time too long — risk of thermal degradation. Use a smaller barrel machine, increase shot weight, or reduce cycle time.' },
    gate_shear_rate:     { severity: 'red',   fix: 'Gate shear rate too high — risk of splay, burning or degradation. Increase gate dimensions or reduce injection speed.' },
    clamp_force:         { severity: 'red',   fix: 'Required clamp force exceeds machine capacity. Use a larger tonnage machine or reduce injection pressure.' },
    hold_vs_gate_freeze: { severity: 'amber', fix: 'Hold time less than gate freeze time — backflow may occur. Increase total hold time or reduce gate depth.' },
    recovery_vs_cooling: { severity: 'amber', fix: 'Recovery time exceeds cooling time — cycle will be extended. Increase screw speed or reduce shot weight.' },
    shot_barrel_ratio:   { severity: 'amber', fix: 'Shot/barrel ratio outside 20–80%. Below 20%: smaller barrel. Above 80%: larger barrel.' },
    cooling_turbulence:  { severity: 'amber', fix: 'Flow is not turbulent in all zones (Re < 4000). Increase coolant pump pressure or flow rate, reduce circuit diameter to raise velocity, or shorten circuit length. Turbulent flow is critical for effective heat transfer.' },
  };

  const hasRedFail = Object.entries(checks).some(([key, check]) =>
    check.pass === false && checkDetails[key]?.severity === 'red'
  );
  const bannerColor  = !anyFail ? '#14532d' : hasRedFail ? '#7f1d1d' : '#2d1f00';
  const bannerBorder = !anyFail ? '#166534' : hasRedFail ? '#991b1b' : '#92400e';

  const hasMachineNotes  = !!machine?.machine_notes;
  const hasMouldNotes    = !!mould?.mould_notes;

  const steps = [
    { key: 'recipe',    label: 'Recipe' },
    { key: 'checks',    label: 'Safety Checks' },
    { key: 'temps',     label: 'Temperatures' },
    { key: 'injection', label: 'Injection' },
    { key: 'packing',   label: 'Packing' },
    { key: 'cooling',   label: 'Cooling' },
    { key: 'plasticising', label: 'Plasticising' },
    { key: 'clamp',     label: 'Clamp Force' },
    { key: 'closing',   label: 'Mould Closing' },
    { key: 'opening',   label: 'Mould Opening' },
    { key: 'ejection',  label: 'Ejection' },
    { key: 'notes',     label: 'Notes & Cycle' },
    { key: 'machine_check', label: 'Machine Checklist' },
    { key: 'mould_check',   label: 'Mould Checklist' },
    { key: 'fai',           label: 'FAI Checklist' },
  ];

  // Section components
  const RecipeSection = () => (
    <div style={s.section}>
      <div style={s.sectionTitle}>Recipe — {recipe?.name}</div>
      {recipe?.description && <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>{recipe.description}</div>}
      <div style={s.paramRow}>
        <span style={s.paramLabel}>Base material</span>
        <span style={{ ...s.paramValue, color: '#14b8a6' }}>{recipe?.material?.name}</span>
      </div>
      {(recipe?.recipe_components || []).map(c => (
        <div key={c.id} style={s.paramRow}>
          <span style={s.paramLabel}>
            <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', marginRight: '6px',
              background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534', color: '#fff' }}>
              {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
            </span>
            {c.material?.name}
          </span>
          <span style={s.paramValue}>{c.percentage}%</span>
        </div>
      ))}
      {(!recipe?.recipe_components || recipe.recipe_components.length === 0) && (
        <div style={{ fontSize: '11px', color: '#6b7280', paddingTop: '4px' }}>Base material only</div>
      )}
    </div>
  );

  const ChecksSection = () => (
    <div style={s.section}>
      <div style={{ ...s.banner, background: bannerColor, border: `1px solid ${bannerBorder}`, marginBottom: '12px' }}>
        <span style={{ fontSize: '16px', fontWeight: '700' }}>
          {anyFail ? '⚠ Issues found — review checks below' : '✓ All checks passed — ready to set up'}
        </span>
      </div>
      <div style={s.checksGrid}>
        {Object.entries(checks).map(([key, check]) => {
          const detail = checkDetails[key] || {};
          const isExpanded = expandedCheck === key;
          const severity = detail.severity || 'amber';
          return (
            <div key={key} onClick={() => setExpandedCheck(isExpanded ? null : key)}
              style={{ ...s.checkCard, cursor: 'pointer',
                background: check.pass === false ? (severity === 'red' ? '#3b0f0f' : '#2d1f00') : '#0f2d1f',
                border: `1px solid ${check.pass === false ? (severity === 'red' ? '#991b1b' : '#92400e') : '#166534'}`,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px', color: check.pass === false ? (severity === 'red' ? '#ef4444' : '#f59e0b') : '#22c55e' }}>
                  {check.pass === true ? '✓' : check.pass === false ? (severity === 'red' ? '✗' : '⚠') : '—'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{check.value}</div>
                </div>
                <span style={{ fontSize: '10px', color: '#6b7280' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>
              {isExpanded && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #2a3040' }}>
                  {check.pass === false ? (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: severity === 'red' ? '#fca5a5' : '#fde68a', marginBottom: '4px' }}>
                        {severity === 'red' ? '🔴 Requires mechanical/physical change' : '🟡 Fixable by changing controller settings'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#d1d5db', lineHeight: '1.5' }}>{detail.fix}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#86efac' }}>✓ This check passed — no action required.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const TempsSection = () => (
    <Section title="Barrel Zone Temperatures" info={INFO.zones}>
      <div style={s.zoneRow}>
        {result.zones.map((z, i) => (
          <div key={i} style={s.zoneCard}>
            <div style={s.zoneLabel}>{z.label}</div>
            <div style={{ ...s.zoneTemp, fontSize: `${fontSize * 1.4}em` }}>{z.temp}°C</div>
          </div>
        ))}
      </div>
      <ParamRow label="Mould temperature" value={`${result.mould_temp}°C`} info={INFO.mould_temp} color="#14b8a6" />
      <ParamRow label="Estimated melt temperature" value={`${result.melt_temp_estimated}°C`} info={INFO.melt_temp}
        color={result.checks?.melt_in_window?.pass ? '#22c55e' : '#ef4444'}
        range={`Processing window: ${result.melt_temp_window?.min}–${result.melt_temp_window?.max}°C`} />
    </Section>
  );

  const InjectionSection = () => (
    <Section title="Injection" info={INFO.inj_pressure}>
      <ParamRow label="Fill time"              value={`${result.fill_time_s}s`}           info={INFO.fill_time} />
      <ParamRow label="Max injection pressure" value={`${result.inj_pressure_bar} bar`}   info={INFO.inj_pressure} color="#14b8a6" range={`Machine max: ${machine?.max_inj_bar} bar`} />
      <ParamRow label="Shot volume"            value={`${result.shot_vol_cm3} cm³`}       info={INFO.shot_vol} />
      <ParamRow label="Dosing stroke"          value={`${result.dosing_stroke_mm} mm`}    info={INFO.dosing_stroke} range={`Barrel: ${machine?.barrel_cm3} cm³`} />
      <ParamRow label={`V→P switchover (${result.vp_type})`} value={result.vp_value}     info={INFO.vp} color="#14b8a6" />
      {result.shear_rate && (
        <ParamRow label="Gate shear rate" value={`${result.shear_rate.toLocaleString()} s⁻¹`} info={INFO.shear_rate}
          color={result.checks?.gate_shear_rate?.pass ? '#22c55e' : '#ef4444'}
          range={`Material max: ${result.shear_limit?.toLocaleString()} s⁻¹`} />
      )}
      <StageTable headers={['#','Speed %','Pressure bar','Until mm']} info={INFO.inj_stages}
        rows={result.inj_stages.map(st => [st.stage, st.speed_pct, st.pressure_bar, st.until_mm])} />
    </Section>
  );

  const PackingSection = () => (
    <Section title="Packing" info={INFO.gate_freeze}>
      <ParamRow label="Gate freeze time" value={`${result.gate_freeze_s}s`} info={INFO.gate_freeze} />
      <ParamRow label="Total hold time"  value={`${result.hold_total_s}s`}  info={INFO.hold_stages} color="#14b8a6" />
      <ParamRow label="Suckback"         value={`${result.suckback_mm} mm`} info={INFO.suckback} />
      <StageTable headers={['#','Pressure bar','Time s']} info={INFO.hold_stages}
        rows={result.hold_stages.map(st => [st.stage, st.pressure_bar, st.time_s])} />
    </Section>
  );

  const CoolingSection = () => (
    <Section title="Cooling" info={INFO.cooling_min}>
      <ParamRow label="Minimum cooling time"     value={result.cooling_min_s ? `${result.cooling_min_s}s` : '—'} info={INFO.cooling_min} />
      <ParamRow label="Recommended cooling time" value={result.cooling_rec_s ? `${result.cooling_rec_s}s` : '—'} info={INFO.cooling_rec} color="#14b8a6" />
      <ParamRow label="Flow rate per circuit"    value={`${result.flow_rate_l_min} L/min`} info={INFO.flow_rate} />
      {result.cooling_analysis && (() => {
        const ca = result.cooling_analysis;
        return (
          <>
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #2a3040' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em', marginBottom: '8px' }}>Heat load</div>
              <ParamRow label="Heat per cycle"   value={`${ca.heat_per_cycle_j.toLocaleString()} J`} />
              <ParamRow label="Avg heat load"    value={`${ca.heat_load_kw} kW`} />
              <ParamRow label="Heat load / hour" value={`${ca.heat_load_per_hr_kj} kJ/hr`} />
            </div>
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #2a3040' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em', marginBottom: '8px' }}>Circuit analysis — per zone</div>
              {ca.zones.map((zone, i) => (
                <div key={i} style={{ background: '#0f1117', border: `1px solid ${zone.turbulent ? '#166534' : zone.flow_regime === 'laminar' ? '#991b1b' : '#92400e'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', textTransform: 'capitalize' }}>{zone.location.replace(/-/g, ' ')}</span>
                    <span style={{ fontSize: '10px', fontWeight: '700', color: zone.turbulent ? '#22c55e' : zone.flow_regime === 'laminar' ? '#ef4444' : '#f59e0b' }}>
                      {zone.flow_regime.toUpperCase()} · Re {zone.reynolds_number.toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      ['Rec. temp',      `${zone.recommended_temp_c}°C`,                    '#f59e0b'],
                      ['Flow/circuit',   `${zone.flow_rate_l_min_per_circuit} L/min`,       '#14b8a6'],
                      ['Velocity',       `${zone.flow_velocity_m_s} m/s`,                   '#f0f2f5'],
                      ['Pressure drop',  `${zone.pressure_drop_bar} bar`,                   '#f0f2f5'],
                      ['Circuits',       String(zone.circuits),                              '#f0f2f5'],
                      ['⌀ × length',     `${zone.diameter_mm}mm × ${zone.length_mm}mm`,     '#f0f2f5'],
                    ].map(([label, val, color]) => (
                      <div key={label} style={{ fontSize: '10px', color: '#6b7280' }}>
                        <div>{label}</div>
                        <div style={{ color, fontWeight: '700', fontFamily: 'monospace', marginTop: '2px' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      })()}
    </Section>
  );

  const PlasticisingSection = () => (
    <Section title="Plasticising" info={INFO.screw_speed}>
      <ParamRow label="Screw speed" value={`${result.screw_speed_rpm} rpm`} info={INFO.screw_speed} color="#14b8a6"
        range={`Machine × material overlap: ${result.screw_speed_overlap} rpm`} />
      <ParamRow label="Back pressure" value={`${result.back_pressure_bar} bar`} info={INFO.back_pressure} color="#14b8a6"
        range={`Machine × material overlap: ${result.back_pressure_overlap} bar`} />
      <ParamRow label="Recovery time" value={`${result.recovery_time_s}s`} info={INFO.recovery}
        color={result.checks?.recovery_vs_cooling?.pass ? '#22c55e' : '#f59e0b'} />
      <ParamRow label="Shot / barrel ratio" value={`${result.shot_barrel_ratio}%`} info={INFO.shot_barrel}
        color={result.checks?.shot_barrel_ratio?.pass ? '#22c55e' : '#f59e0b'} range="Ideal range: 20–80%" />
      {result.residence_time_s && (
        <ParamRow label="Residence time" value={`${result.residence_time_s}s`} info={INFO.residence}
          color={result.checks?.residence_time?.pass ? '#22c55e' : '#ef4444'} range="Max: 360s" />
      )}
    </Section>
  );

  const ClampSection = () => (
    <Section title="Clamp Force" info={INFO.clamp}>
      <ParamRow label="Minimum clamp force"   value={result.clamp_min_kn   ? `${result.clamp_min_kn} kN`   : '—'} info={INFO.clamp} />
      <ParamRow label="Suggested clamp force" value={result.clamp_force_kn ? `${result.clamp_force_kn} kN` : '—'} info={INFO.clamp_sugg} color="#14b8a6" />
    </Section>
  );

  const ClosingSection = () => (
    <Section title="Mould Closing" info={INFO.closing}>
      <StageTable headers={['#','Pressure bar','Speed mm/s','Until mm','Note']} info={INFO.closing}
        rows={result.closing_stages.map(st => [st.stage, st.pressure_bar, st.speed_mm_s, st.until_mm, st.note])} />
    </Section>
  );

  const OpeningSection = () => (
    <Section title="Mould Opening" info={INFO.opening}>
      <StageTable headers={['#','Pressure bar','Speed mm/s','Until mm','Note']} info={INFO.opening}
        rows={result.opening_stages.map(st => [st.stage, st.pressure_bar, st.speed_mm_s, st.until_mm, st.note])} />
    </Section>
  );

  const EjectionSection = () => (
    <Section title="Ejection" info={INFO.ejection}>
      <ParamRow label="Delay after open" value={`${result.ejector_delay_s}s`}     info={INFO.ejection} />
      <ParamRow label="Ejector stroke"   value={`${result.ejector_stroke_mm} mm`} info={INFO.ej_stroke} />
      <ParamRow label="Forward speed"    value={`${result.ejector_speed} mm/s`}   info={INFO.ej_fwd} color="#14b8a6" />
      <ParamRow label="Retract speed"    value={`${result.ejector_retract} mm/s`} info={INFO.ej_ret} />
      <ParamRow label="Pulses"           value={result.ejector_pulses}             info={INFO.ej_pulses} />
    </Section>
  );

  const NotesSection = () => (
    <div>
      <div style={s.section}>
        <div style={s.sectionTitle}>Cycle Summary</div>
        <CycleWaterfall result={result} fontSize={fontSize} />
      </div>
      {(hasMachineNotes || hasMouldNotes) && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Notes</div>
          {hasMachineNotes && (
            <div style={{ ...s.infoBox, marginBottom: '10px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#93c5fd', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>ℹ Machine — {machine?.name}</div>
              <div style={{ fontSize: '12px', color: '#bfdbfe', lineHeight: '1.6' }}>{machine.machine_notes}</div>
            </div>
          )}
          {hasMouldNotes && (
            <div style={s.infoBox}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#93c5fd', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>ℹ Mould — {mould?.ref}</div>
              <div style={{ fontSize: '12px', color: '#bfdbfe', lineHeight: '1.6' }}>{mould.mould_notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const stepComponents = [
    <RecipeSection />, <ChecksSection />, <TempsSection />,
    <InjectionSection />, <PackingSection />, <CoolingSection />,
    <PlasticisingSection />, <ClampSection />, <ClosingSection />,
    <OpeningSection />, <EjectionSection />, <NotesSection />,
    <Checklist type="machine" entityId={machine?.id} entityName={machine?.name} />,
    <Checklist type="mould"   entityId={mould?.id}   entityName={`${mould?.ref} — ${mould?.name}`} />,
    <Checklist type="fai"     entityId={recipe?.id}  entityName={recipe?.name} />,
  ];

  return (
    <div style={{ ...s.page, fontSize: `${fontSize}em` }}>
      {/* Header */}
      <div style={s.resultsHeader}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button style={s.backBtn} onClick={onBack}>← Back</button>
          <button style={s.backBtn} onClick={onNew}>New Calculation</button>
          <button style={s.backBtn} onClick={onReturnToLanding}>⌂ Home</button>
          <button style={{ ...s.goldenBtn, opacity: 1, background: goldenMarked ? '#166534' : '#92400e' }}
            onClick={onMarkGolden}>
            {goldenMarked ? '⭐ Golden Run Saved — Undo?' : '⭐ Mark as Golden Run'}
          </button>
          
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View toggle */}
          <div style={s.viewToggle}>
            <button style={{ ...s.viewBtn, ...(viewMode === 'full' ? s.viewBtnActive : {}) }} onClick={() => setViewMode('full')}>Full view</button>
            <button style={{ ...s.viewBtn, ...(viewMode === 'steps' ? s.viewBtnActive : {}) }} onClick={() => { setViewMode('steps'); setCurrentStep(0); }}>Step by step</button>
          </div>
          {/* Font size */}
          <div style={s.fontControls}>
            <span style={s.fontLabel}>A:</span>
            <button style={{ ...s.fontBtn, ...(currentFont === 'normal' ? s.fontBtnActive : {}) }}
              onClick={() => { onFontChange('normal'); document.body.style.zoom = '100%'; }}>A</button>
            <button style={{ ...s.fontBtn, ...(currentFont === 'large' ? s.fontBtnActive : {}) }}
              onClick={() => { onFontChange('large'); document.body.style.zoom = '125%'; }}>A+</button>
            <button style={{ ...s.fontBtn, ...(currentFont === 'xl' ? s.fontBtnActive : {}) }}
              onClick={() => { onFontChange('xl'); document.body.style.zoom = '150%'; }}>A++</button>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <span style={s.breadcrumbItem}>{machine?.name}</span>
        <span style={s.breadcrumbArrow}>→</span>
        <span style={s.breadcrumbItem}>{mould?.ref}</span>
        <span style={s.breadcrumbArrow}>→</span>
        <span style={s.breadcrumbItem}>{recipe?.name}</span>
      </div>

      {/* FULL VIEW */}
      {viewMode === 'full' && (
        <div>
          <div style={s.legend}>
            <span style={s.legendTitle}>Colours:</span>
            <LegendItem color="#f59e0b" label="Zone temp" />
            <LegendItem color="#14b8a6" label="Recommended" />
            <LegendItem color="#22c55e" label="Good range" />
            <LegendItem color="#ef4444" label="Critical" />
            <LegendItem color="#f0f2f5" label="Neutral" />
          </div>
          <RecipeSection />
          <ChecksSection />
          <TempsSection />
          <InjectionSection />
          <PackingSection />
          <CoolingSection />
          <PlasticisingSection />
          <ClampSection />
          <ClosingSection />
          <OpeningSection />
          <EjectionSection />
          <NotesSection />
          {/* Checklist buttons */}
          <div style={s.checklistBtns}>
            <button style={s.checklistBtn} onClick={() => setShowChecklist('machine')}>📋 Machine Checklist</button>
            <button style={s.checklistBtn} onClick={() => setShowChecklist('mould')}>📋 Mould Checklist</button>
            <button style={s.checklistBtn} onClick={() => setShowChecklist('fai')}>📋 FAI Checklist</button>
          </div>
          {showChecklist && (
            <div style={s.overlay} onClick={() => setShowChecklist(null)}>
              <div style={s.modal} onClick={e => e.stopPropagation()}>
                <div style={s.modalHeader}>
                  <div style={s.modalTitle}>
                    {showChecklist === 'machine' ? `Machine Checklist — ${machine?.name}` :
                     showChecklist === 'mould'   ? `Mould Checklist — ${mould?.ref}` :
                     `FAI Checklist — ${recipe?.name}`}
                  </div>
                  <button style={s.closeBtn} onClick={() => setShowChecklist(null)}>✕</button>
                </div>
                <div style={s.modalBody}>
                  {showChecklist === 'machine' && <Checklist type="machine" entityId={machine?.id} entityName={machine?.name} />}
                  {showChecklist === 'mould'   && <Checklist type="mould"   entityId={mould?.id}   entityName={`${mould?.ref} — ${mould?.name}`} />}
                  {showChecklist === 'fai'     && <Checklist type="fai"     entityId={recipe?.id}  entityName={recipe?.name} />}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP BY STEP VIEW */}
      {viewMode === 'steps' && (
        <div>
          {/* Step indicator */}
          <div style={s.stepNav}>
            <div style={s.stepProgress}>
              <span style={s.stepProgressLabel}>{steps[currentStep].label}</span>
              <span style={s.stepProgressCount}>{currentStep + 1} / {steps.length}</span>
            </div>
            <div style={s.stepProgressBar}>
              <div style={{ ...s.stepProgressFill, width: `${((currentStep + 1) / steps.length) * 100}%` }} />
            </div>
          </div>

          {/* Step jump menu */}
          <div style={s.stepMenu}>
            {steps.map((step, i) => (
              <button key={step.key}
                style={{ ...s.stepMenuBtn, ...(i === currentStep ? s.stepMenuBtnActive : {}) }}
                onClick={() => setCurrentStep(i)}>
                {step.label}
              </button>
            ))}
          </div>

          {/* Current step content */}
          <div style={{ minHeight: '300px' }}>
            {stepComponents[currentStep]}
          </div>

          {/* Spacer so content doesn't hide behind fixed footer */}
          <div style={{ height: '144px' }} />

          {/* Fixed navigation footer */}
          <div style={s.stepFooter}>
            <button style={s.stepFooterBtn} onClick={() => setCurrentStep(c => Math.max(0, c - 1))} disabled={currentStep === 0}>
              ← Previous
            </button>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{currentStep + 1} / {steps.length}</span>
            <button style={{ ...s.stepFooterBtn, background: '#14b8a6', color: '#fff' }}
              onClick={() => currentStep === steps.length - 1 ? setViewMode('full') : setCurrentStep(c => c + 1)}>
              {currentStep === steps.length - 1 ? 'Go to full view' : 'Next →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CYCLE WATERFALL ───────────────────────────────────────────
function CycleWaterfall({ result, fontSize }) {
  const [expanded, setExpanded] = useState(null);

  const segments = [
    {
      key: 'close', label: 'Close', color: '#6366f1',
      time: result.closing_stages ? result.closing_stages.reduce((s, st) => s + (st.until_mm / Math.max(st.speed_mm_s, 1)), 0).toFixed(1) : '—',
      detail: `${result.closing_stages?.length || 0} stages — fast close, slow approach, high-pressure lock`,
    },
    { key: 'fill',     label: 'Fill',     color: '#3b82f6', time: result.fill_time_s2,   detail: `${result.inj_stages?.length || 0} injection stages at up to ${result.inj_pressure_bar} bar` },
    { key: 'hold',     label: 'Hold',     color: '#8b5cf6', time: result.hold_time_s,    detail: `${result.hold_stages?.length || 0} hold stages — gate freeze: ${result.gate_freeze_s}s` },
    { key: 'cool',     label: 'Cool',     color: '#14b8a6', time: result.cool_time_s,    detail: `Min: ${result.cooling_min_s}s — Recommended: ${result.cooling_rec_s}s` },
    { key: 'recover',  label: 'Recovery', color: '#f59e0b', time: result.recovery_s,     detail: `Screw recovery at ${result.screw_speed_rpm} rpm — ${result.shot_barrel_ratio}% of barrel` },
    {
      key: 'open', label: 'Open', color: '#6366f1',
      time: result.opening_stages ? result.opening_stages.reduce((s, st) => s + (st.until_mm / Math.max(st.speed_mm_s, 1)), 0).toFixed(1) : '—',
      detail: `${result.opening_stages?.length || 0} stages — slow break, fast open, decelerate`,
    },
    {
      key: 'eject', label: 'Eject', color: '#ec4899',
      time: result.ejector_stroke_mm && result.ejector_speed ? ((result.ejector_stroke_mm / result.ejector_speed) * 2 * result.ejector_pulses + result.ejector_delay_s).toFixed(1) : '—',
      detail: `Delay: ${result.ejector_delay_s}s — ${result.ejector_pulses} pulse(s) at ${result.ejector_speed} mm/s`,
    },
  ];

  const total = segments.reduce((sum, seg) => sum + (parseFloat(seg.time) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', height: '52px', marginBottom: '8px' }}>
        {segments.map(seg => {
          const t = parseFloat(seg.time) || 0;
          const pct = total > 0 ? (t / total) * 100 : 0;
          return (
            <div key={seg.key} onClick={() => setExpanded(expanded === seg.key ? null : seg.key)}
              style={{ flex: Math.max(pct, 3), background: seg.color, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'flex 0.3s', minWidth: '30px', overflow: 'hidden' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{seg.label}</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#fff' }}>{String(seg.time).replace('.', ',')}s</div>
            </div>
          );
        })}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', fontSize: '12px', color: '#9ca3af' }}>
          <strong style={{ color: '#f0f2f5' }}>{segments.find(s => s.key === expanded)?.label}:</strong>{' '}
          {segments.find(s => s.key === expanded)?.detail}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>Estimated total cycle time</span>
        <span style={{ fontSize: `${fontSize * 1.1}em`, fontWeight: '700', color: '#14b8a6' }}>{total.toFixed(1).replace('.', ',')}s</span>
      </div>
      <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '2px' }}>Click any segment to see details. Mould movement times estimated from speed and stroke data.</div>
    </div>
  );
}

// ── SHARED COMPONENTS ─────────────────────────────────────────
function Section({ title, info, children }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div style={s.section}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={s.sectionTitle}>{title}</div>
        {info && (
          <span style={s.infoIcon} onClick={() => setShowInfo(v => !v)} title={info}>ℹ</span>
        )}
      </div>
      {showInfo && info && (
        <div style={s.infoBox}>{info}</div>
      )}
      {children}
    </div>
  );
}

function ParamRow({ label, value, info, color, color2, range }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div style={s.paramRow}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={s.paramLabel}>{label}</span>
        {info && <span style={s.infoIcon} onClick={() => setShowTip(v => !v)}>ℹ</span>}
        {showTip && <div style={s.tooltip}>{info}{range ? ` · ${range}` : ''}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {range && !showTip && <span style={{ fontSize: '10px', color: '#4b5563' }}>{range}</span>}
        <span style={{ ...s.paramValue, color: color || '#f0f2f5' }}>{value}</span>
      </div>
    </div>
  );
}

function StageTable({ headers, rows, info }) {
  const [showInfo, setShowInfo] = useState(false);
  const cols = headers.length;
  const gridCols = cols === 4 ? '30px 1fr 1fr 1fr' : '30px 1fr 1fr 1fr 2fr';
  return (
    <div style={{ marginTop: '10px' }}>
      {info && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>Stages</span>
          <span style={s.infoIcon} onClick={() => setShowInfo(v => !v)}>ℹ</span>
        </div>
      )}
      {showInfo && <div style={s.infoBox}>{info}</div>}
      <div style={{ ...s.stageHeader, gridTemplateColumns: gridCols }}>
        {headers.map((h, i) => <span key={i}>{h}</span>)}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ ...s.stageRow, gridTemplateColumns: gridCols }}>
          {row.map((cell, j) => <span key={j} style={j === row.length - 1 && cols === 5 ? { color: '#6b7280', fontSize: '10px' } : {}}>{cell}</span>)}
        </div>
      ))}
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: '10px', color: '#9ca3af' }}>{label}</span>
    </div>
  );
}

// ── MATERIAL PHYSICS MODAL ────────────────────────────────────
function MaterialPhysicsModal({ material, onSaved, onClose }) {
  const [form, setForm] = useState({
    tmelt_min:  String(material?.tmelt_min  ?? ''),
    tmelt_max:  String(material?.tmelt_max  ?? ''),
    tmould_rec: String(material?.tmould_rec ?? ''),
    t_ejection: String(material?.t_ejection ?? ''),
    alpha:      String(material?.alpha      ?? ''),
    cp:         String(material?.cp         ?? ''),
    density:    String(material?.density    ?? ''),
    mfi:        String(material?.mfi        ?? ''),
    mfi_cond:   material?.mfi_cond          || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function save() {
    if (!form.tmelt_min || !form.tmelt_max || !form.alpha || !form.cp || !form.density) {
      setError('Melt temp range, α, Cp and density are all required'); return;
    }
    setSaving(true);
    const payload = { ...form };
    ['tmelt_min','tmelt_max','tmould_rec','t_ejection','alpha','cp','density','mfi'].forEach(k => {
      payload[k] = payload[k] !== '' ? parseFloat(payload[k]) : null;
    });
    api.put(`/api/v1/materials/${material.id}`, payload)
      .then(() => onSaved())
      .catch(() => { setError('Save failed'); setSaving(false); });
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>Complete Material Data</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{material.name} — add missing physics constants to run the calculation</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {error && <div style={{ ...s.errorBox, marginBottom: '12px' }}>{error}</div>}

          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#ef4444', letterSpacing: '0.06em', marginBottom: '8px' }}>Required for calculation</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <QField label="Melt temp min (°C) *"            value={form.tmelt_min}  onChange={v => set('tmelt_min', v)}  type="number" />
            <QField label="Melt temp max (°C) *"            value={form.tmelt_max}  onChange={v => set('tmelt_max', v)}  type="number" />
            <QField label="Thermal diffusivity α (m²/s) *"  value={form.alpha}      onChange={v => set('alpha', v)}      />
            <QField label="Specific heat Cp (J/kg·K) *"     value={form.cp}         onChange={v => set('cp', v)}         type="number" />
            <QField label="Melt density (kg/m³) *"          value={form.density}    onChange={v => set('density', v)}    type="number" />
            <QField label="MFI (g/10min)"                   value={form.mfi}        onChange={v => set('mfi', v)}        type="number" />
          </div>

          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em', marginBottom: '8px' }}>Recommended (improves accuracy)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <QField label="Mould temp rec. (°C)"  value={form.tmould_rec} onChange={v => set('tmould_rec', v)} type="number" />
            <QField label="Ejection temp (°C)"    value={form.t_ejection} onChange={v => set('t_ejection', v)} type="number" />
            <QField label="MFI conditions"         value={form.mfi_cond}   onChange={v => set('mfi_cond', v)} />
          </div>
        </div>
        <div style={s.modalFooter}>
          <button style={s.backBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
            {saving ? 'Saving...' : 'Save & Recalculate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const s = {
  page:          { padding: '20px', fontFamily: 'system-ui, sans-serif', background: '#0f1117', minHeight: '100vh', color: '#f0f2f5' },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title:         { fontSize: '22px', fontWeight: '700', margin: '0 0 4px 0' },
  sub:           { fontSize: '13px', color: '#6b7280', margin: 0 },
  newCalcCard:   { background: '#1e2329', border: '2px solid #14b8a6', borderRadius: '12px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', marginBottom: '28px' },
  newCalcIcon:   { fontSize: '32px' },
  newCalcTitle:  { fontSize: '16px', fontWeight: '700', marginBottom: '4px' },
  newCalcSub:    { fontSize: '12px', color: '#6b7280' },
  newCalcArrow:  { marginLeft: 'auto', fontSize: '20px', color: '#14b8a6' },
  sectionTitle:  { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#14b8a6', marginBottom: '12px' },
  goldenGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' },
  goldenCard:    { background: '#1e2329', border: '1px solid #92400e', borderRadius: '10px', padding: '14px' },
  goldenRef:     { fontSize: '11px', fontWeight: '700', color: '#fbbf24', fontFamily: 'monospace', marginBottom: '2px' },
  goldenName:    { fontSize: '13px', fontWeight: '700', marginBottom: '4px' },
  goldenMeta:    { fontSize: '11px', color: '#6b7280' },
  goldenDate:    { fontSize: '10px', color: '#4b5563', marginTop: '4px' },
  emptyBox:      { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '32px', textAlign: 'center' },
  emptyIcon:     { fontSize: '32px', marginBottom: '8px' },
  emptyText:     { fontSize: '14px', fontWeight: '700', color: '#6b7280', marginBottom: '4px' },
  emptySub:      { fontSize: '12px', color: '#4b5563' },
  wizardHeader:  { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' },
  stepIndicator: { display: 'flex', gap: '6px', marginTop: '6px' },
  stepDot:       { width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700' },
  stepLabel:     { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: '12px' },
  selectGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '12px' },
  selectCard:    { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '14px', cursor: 'pointer' },
  selectCardTitle: { fontSize: '13px', fontWeight: '700', marginBottom: '4px' },
  selectCardSub: { fontSize: '11px', color: '#6b7280' },
  addNewBtn:     { background: 'none', border: '1px dashed #2a3040', color: '#6b7280', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', marginTop: '4px' },
  addComponentBtn: { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer' },
  calculating:   { textAlign: 'center', padding: '32px', color: '#6b7280', fontSize: '13px' },
  errorBox:      { background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '12px', fontSize: '13px', marginTop: '12px' },
  overlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:         { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' },
  modalTitle:    { fontSize: '17px', fontWeight: '700' },
  closeBtn:      { background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' },
  modalBody:     { padding: '16px 20px' },
  modalFooter:   { display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #2a3040' },
  formField:     { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  formLabel:     { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' },
  formInput:     { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px' },
  backBtn:       { background: '#1e2329', color: '#9ca3af', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer' },
  saveBtn:       { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  resultsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' },
  fontControls:  { display: 'flex', alignItems: 'center', gap: '6px' },
  fontLabel:     { fontSize: '11px', color: '#6b7280' },
  fontBtn:       { background: '#1e2329', border: '1px solid #2a3040', color: '#6b7280', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
  fontBtnActive: { background: '#14b8a6', color: '#fff', border: '1px solid #14b8a6' },
  breadcrumb:    { display: 'flex', alignItems: 'center', gap: '8px', background: '#1e2329', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', flexWrap: 'wrap' },
  breadcrumbItem: { fontSize: '12px', fontWeight: '600', color: '#14b8a6' },
  breadcrumbArrow: { color: '#4b5563' },
  legend:        { display: 'flex', alignItems: 'center', gap: '16px', background: '#1e2329', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', flexWrap: 'wrap' },
  legendTitle:   { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em' },
  banner:        { borderRadius: '10px', padding: '14px 18px', marginBottom: '16px' },
  section:       { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '16px', marginBottom: '12px' },
  checksGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' },
  checkCard:     { padding: '12px', borderRadius: '8px' },
  zoneRow:       { display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' },
  zoneCard:      { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 14px', flex: 1, minWidth: '80px', textAlign: 'center' },
  zoneLabel:     { fontSize: '9px', color: '#6b7280', marginBottom: '4px' },
  zoneTemp:      { fontWeight: '700', fontFamily: 'monospace', color: '#f59e0b' },
  paramRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1a2030', position: 'relative' },
  paramLabel:    { fontSize: '12px', color: '#6b7280' },
  paramValue:    { fontSize: '13px', fontWeight: '700', fontFamily: 'monospace' },
  infoIcon:      { fontSize: '11px', color: '#3b82f6', cursor: 'pointer', userSelect: 'none', background: '#1d2d4a', border: '1px solid #1d4ed8', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  infoBox:       { background: '#1e2d45', border: '1px solid #1d4ed8', borderRadius: '8px', padding: '10px 12px', fontSize: '11px', color: '#93c5fd', lineHeight: '1.6', marginBottom: '10px' },
  tooltip:       { position: 'absolute', left: 0, top: '100%', zIndex: 50, background: '#1e2d45', border: '1px solid #1d4ed8', borderRadius: '8px', padding: '8px 12px', fontSize: '11px', color: '#93c5fd', lineHeight: '1.5', maxWidth: '300px', whiteSpace: 'normal' },
  stageHeader:   { display: 'grid', gap: '8px', padding: '6px 8px', background: '#0f1117', borderRadius: '6px 6px 0 0', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em' },
  stageRow:      { display: 'grid', gap: '8px', padding: '8px', borderTop: '1px solid #2a3040', fontSize: '12px', fontFamily: 'monospace' },
  center:        { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#6b7280' },
  viewToggle:       { display: 'flex', background: '#1e2329', border: '1px solid #2a3040', borderRadius: '8px', overflow: 'hidden' },
  viewBtn:          { background: 'none', border: 'none', color: '#6b7280', padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  viewBtnActive:    { background: '#14b8a6', color: '#fff' },
  checklistBtns:    { display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' },
  checklistBtn:     { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', cursor: 'pointer', flex: 1, minWidth: '160px' },
  stepNav:          { marginBottom: '12px' },
  stepProgress:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  stepProgressLabel: { fontSize: '16px', fontWeight: '700', color: '#f0f2f5' },
  stepProgressCount: { fontSize: '12px', color: '#6b7280' },
  stepProgressBar:  { height: '4px', background: '#2a3040', borderRadius: '2px', overflow: 'hidden' },
  stepProgressFill: { height: '100%', background: '#14b8a6', borderRadius: '2px', transition: 'width 0.3s ease' },
  stepMenu:         { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' },
  stepMenuBtn:      { background: '#1e2329', border: '1px solid #2a3040', color: '#6b7280', borderRadius: '6px', padding: '4px 10px', fontSize: '10px', fontWeight: '700', cursor: 'pointer' },
  stepMenuBtnActive: { background: '#14b8a6', color: '#fff', border: '1px solid #14b8a6' },
  stepFooter:    { position: 'fixed', bottom: '64px', left: 0, right: 0, background: '#1e2329', borderTop: '1px solid #2a3040', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 150 },
  stepFooterBtn: { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  goldenBtn: { background: '#92400e', color: '#fde68a', border: '1px solid #b45309', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
};